import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";
import confetti from "canvas-confetti";
import { paymentStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const PaymentResult = ({ status }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState(status === "cancel" ? "cancelled" : "checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (status !== "success" || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          await refresh();
          setState(res.is_gift ? "gifted" : "paid");
          confetti({ particleCount: 160, spread: 100, origin: { y: 0.6 } });
          return;
        }
      } catch { /* keep trying */ }
      if (tries >= 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [status, sessionId, refresh]);

  return (
    <div className="grain min-h-screen grid place-items-center px-4 relative z-10">
      <div className="max-w-md w-full text-center rounded-3xl border border-border bg-card shadow-lg p-10">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-accent" />
            <h1 className="mt-6 font-serif text-3xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="grid place-items-center h-16 w-16 mx-auto rounded-2xl bg-accent/15 text-accent">
              <Crown className="h-8 w-8" />
            </div>
            <h1 data-testid="payment-success" className="mt-6 font-serif text-3xl font-bold">You're Pro! 🎉</h1>
            <p className="mt-2 text-muted-foreground">
              Unlimited lectures, AI voice narration, the Professor tutor, Focus Mode and 10-minute quick study are all unlocked.
            </p>
            <button onClick={() => navigate("/")} className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3 font-semibold hover:opacity-90 transition">
              Start Studying
            </button>
          </>
        )}
        {state === "gifted" && (
          <>
            <div className="grid place-items-center h-16 w-16 mx-auto rounded-2xl bg-accent/15 text-accent">
              <Crown className="h-8 w-8" />
            </div>
            <h1 data-testid="payment-gifted" className="mt-6 font-serif text-3xl font-bold">Gift sent! 🎁</h1>
            <p className="mt-2 text-muted-foreground">
              Your recipient now has Lectura Pro. If they don't have an account yet, it unlocks automatically when they sign up with that email.
            </p>
            <button onClick={() => navigate("/")} className="mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3 font-semibold hover:opacity-90 transition">
              Back to Lectura
            </button>
          </>
        )}
        {(state === "cancelled" || state === "timeout") && (
          <>
            <div className="grid place-items-center h-16 w-16 mx-auto rounded-2xl bg-secondary text-muted-foreground">
              <XCircle className="h-8 w-8" />
            </div>
            <h1 className="mt-6 font-serif text-3xl font-bold">
              {state === "cancelled" ? "Checkout cancelled" : "Still processing"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {state === "cancelled" ? "No charge was made. You can upgrade any time." : "Your payment is taking a little longer. Check your dashboard shortly."}
            </p>
            <Link to="/" className="mt-8 inline-block rounded-full bg-primary text-primary-foreground px-8 py-3 font-semibold hover:opacity-90 transition">
              Back to Lectura
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
